<template>
    <div class="set-field-value-process-modal">
        <ProcessModal
            :is-open="isOpen"
            :process-tip-link="toolTipLink"
            :process-tip-text="$t('funnel.set.field.value.pro.tip.text')"
            :summary-text="$t('funnel.set.field.value.summary.text')"
            :title="$t('funnel.set.field.value.modal.title')"
            @close-modal="closeModal"
            @update-config="saveCell"
        >
            <template #processParameters>
                <DsMultiselect
                    v-model="option"
                    bind-value-only
                    :placeholder="$t('funnel.set.field.value.option')"
                    :options="OPTIONS_VALUES"
                    required
                    data-qa="set-field-value-type-select"
                    class="set-field-value-type-select"
                >
                    <template #option="{ option: optionProp, highlight }">
                        <div class="menu-item set-field-value-option">
                            <div class="option-label" :class="{ highlight }">
                                {{ optionProp.label }}
                            </div>
                            <div class="option-description">
                                {{ optionProp.description }}
                            </div>
                        </div>
                    </template>
                </DsMultiselect>

                <div v-if="option" class="field-container">
                    <DsMultiselect
                        v-model="config.fieldName"
                        :label="$t('funnel.set.field.value.field')"
                        :placeholder="$t('funnel.set.field.value.field')"
                        bind-value-only
                        searchable
                        :options="fieldOptions"
                        required
                        class="field-option-select"
                        data-qa="field-option-select"
                    />

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="fieldConfigRender && showAdditionalFieldConfiguration && !isCompanyField && !isPersonNotesField"
                        v-show="!config.useTodaysDate"
                        ref="fieldConfigRender"
                        data-qa="field-config-render"
                        v-html="fieldConfigRender"
                    />
                    <!-- eslint-enable vue/no-v-html -->

                    <!-- TODO: Replace with single DsMultiselect with @update-search. Old design system issues.  -->
                    <template v-else-if="showAdditionalFieldConfiguration && isCompanyField">
                        <div class="company-field-search-container">
                            <DsInputField
                                v-if="!config.fieldValue || !companyNameFieldValue"
                                v-model="companyNameFieldValue"
                                :label="$t('funnel.set.field.value.company.name.label')"
                                :placeholder="$t('funnel.set.field.value.company.name.placeholder')"
                                required
                            />

                            <template v-if="companyNameFieldValue && companyFieldOptions.length">
                                <DsMultiselect
                                    v-model="config.fieldValue"
                                    :loading="companyLoading"
                                    :label="$t('funnel.set.field.value.company.value.label')"
                                    :placeholder="$t('funnel.set.field.value.company.value.placeholder')"
                                    label-prop="name"
                                    value-prop="id"
                                    bind-value-only
                                    searchable
                                    :options="companyFieldOptions"
                                    required
                                />

                                <DsIconButton
                                    v-if="config.fieldValue"
                                    name="x"
                                    @click="config.fieldValue = null"
                                />
                            </template>
                            <div v-if="companyNameFieldValue && !companyLoading && !companyFieldOptions.length" class="no-company-warning">
                                {{ $t('funnel.set.field.value.company.no.results') }}
                            </div>
                        </div>
                    </template>

                    <template v-else-if="showAdditionalFieldConfiguration && isPersonNotesField">
                        <DsTextAreaField
                            v-model="config.fieldValue"
                            class="person-notes-textarea"
                            data-qa="person-notes-textarea"
                            :label="$t('funnel.set.field.value.notes')"
                            :placeholder="$t('funnel.set.field.value.notes')"
                            :maxlength="LIMITS.NOTES_MAXLENGTH"
                            required
                        />
                    </template>

                    <div v-if="option === OPTION_VALUES.MATH" class="math-config-container">
                        <DsMultiselect
                            v-model="config.mathOption"
                            :label="$t('funnel.set.field.value.math')"
                            :placeholder="$t('funnel.set.field.value.math')"
                            bind-value-only
                            data-qa="math-option-select"
                            :options="MATH_OPTIONS_VALUES"
                            required
                        />

                        <DsInputField
                            v-model="config.fieldValue"
                            :label="$t('funnel.set.field.value.quantity')"
                            type="number"
                            required
                            data-qa="math-quantity-input"
                            :maxlength="LIMITS.QUANTITY_MAXLENGTH"
                            :min="LIMITS.MIN_QUANTITY"
                            :max="LIMITS.MAX_QUANTITY"
                        />
                    </div>
                </div>

                <DsCheckbox
                    v-if="showAdditionalFieldConfiguration && isDateField"
                    v-model="config.useTodaysDate"
                    class="date-checkbox"
                    :label="$t('funnel.set.field.value.date')"
                    @input="afterFill"
                />

                <span v-if="showError" data-qa="error-message" class="error-message">
                    {{ $t('funnel.set.field.value.error.required') }}
                </span>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';
import ProcessModal from '../processModal/ProcessModal.vue';
import {
    getContactFields,
    getFieldValueConfig,
    loadFieldValueCode,
    save,
} from './setFieldValue.api';

const COMPANY_INFO = 'companyInfo';
const PERSON_NOTES = 'contactNotes';

const OPTION_VALUES = {
    OVERWRITE: 'overwrite',
    SET: 'set',
    CLEAR: 'clear',
    MATH: 'math',
};

const LIMITS = {
    MIN_QUANTITY: -999999999,
    MAX_QUANTITY: 999999999,
    QUANTITY_MAXLENGTH: 13, // e.g. -100000000.99
};

const FIELD_TYPES = {
    RADIO: 'radio',
    SELECT_ONE: 'select-one',
    SELECT_MULTIPLE: 'select-multiple',
    EMAIL: 'email',
    TEXT_AREA: 'textarea',
    TEXT: 'text',
    HIDDEN: 'hidden',
};

export default {
    components: { ProcessModal },
    props: {
        isOpen: {
            type: Boolean,
            default: () => true,
        },
        cell: {
            type: Object,
            default: () => null,
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value', 'mark-dirty'],
    data() {
        const OPTION_OVERWRITE = { label: this.$t('funnel.set.field.value.overwrite'), description: this.$t('funnel.set.field.value.overwrite.description'), value: 'overwrite' };
        const OPTION_SET = { label: this.$t('funnel.set.field.value.set'), description: this.$t('funnel.set.field.value.set.description'), value: 'set' };
        const OPTION_CLEAR = { label: this.$t('funnel.set.field.value.clear'), description: this.$t('funnel.set.field.value.clear.description'), value: 'clear' };
        const OPTION_MATH = { label: this.$t('funnel.set.field.value.use.math'), description: this.$t('funnel.set.field.value.use.math.description'), value: 'math' };

        const OPTIONS_VALUES = [OPTION_OVERWRITE, OPTION_SET, OPTION_CLEAR, OPTION_MATH];

        const MATH_OPTION_ADD = { label: this.$t('funnel.set.field.value.add'), value: 'add' };
        const MATH_OPTION_SUBTRACT = { label: this.$t('funnel.set.field.value.subtract'), value: 'subtract' };
        const MATH_OPTION_MULTIPLY = { label: this.$t('funnel.set.field.value.multiply'), value: 'multiply' };
        const MATH_OPTION_DIVIDE = { label: this.$t('funnel.set.field.value.divide'), value: 'divide' };

        const MATH_OPTIONS_VALUES = [MATH_OPTION_ADD, MATH_OPTION_SUBTRACT, MATH_OPTION_MULTIPLY, MATH_OPTION_DIVIDE];

        return {
            OPTIONS_VALUES,
            MATH_OPTIONS_VALUES,
            config: {
                fieldName: '',
                fieldValue: '',
                fieldDataType: '',
                overwriteExistingValue: false,
                useTodaysDate: false,
                clearExistingValue: false,
                mathOption: '',
            },
            companyNameFieldValue: null,
            companyFieldOptions: [],
            companyLoading: false,
            updateCompanyFieldOptions: null,
            fields: [],
            optionValue: null,
            fieldConfigRender: '',
            showError: false,
            OPTION_VALUES,
            LIMITS,
        };
    },

    computed: {
        showAdditionalFieldConfiguration() {
            if (!this.config.fieldName) return false;

            return [OPTION_VALUES.OVERWRITE, OPTION_VALUES.SET].includes(this.option);
        },

        isPersonNotesField() {
            return this.config.fieldName === PERSON_NOTES;
        },

        isCompanyField() {
            return this.config.fieldName === COMPANY_INFO;
        },

        isDateField() {
            return ['date', 'datetime'].includes(this.config.fieldDataType);
        },

        fieldOptions() {
            if (this.option === OPTION_VALUES.MATH) {
                return this.fields.filter(({ isNumeric }) => JSON.parse(isNumeric));
            }

            if (this.option === OPTION_VALUES.OVERWRITE) {
                return this.fields.filter(({ label }) => (label !== 'Leadsource'));
            }

            return this.fields;
        },

        option: {
            set(value) {
                if (this.optionValue && this.optionValue !== value) {
                    this.fieldConfigRender = '';
                    this.config = {
                        fieldName: '',
                        fieldValue: '',
                        fieldDataType: '',
                        overwriteExistingValue: false,
                        useTodaysDate: false,
                        clearExistingValue: false,
                        mathOption: '',
                    };
                    this.$emit('mark-dirty');
                }

                this.optionValue = value;

                switch (value) {
                case OPTION_VALUES.OVERWRITE:
                    this.config.overwriteExistingValue = true;
                    break;
                case OPTION_VALUES.CLEAR:
                    this.config.overwriteExistingValue = true;
                    this.config.clearExistingValue = true;
                    break;
                case OPTION_VALUES.MATH:
                    this.config.overwriteExistingValue = true;
                    break;
                default:
                }
            },
            get() {
                return this.optionValue;
            },
        },

        fieldConfigRenderClasses() {
            const classes = [];

            if (this.config.fieldName.includes('customFields')) {
                classes.push(`custom_${this.config.fieldDataType}`);
            } else {
                classes.push(this.config.fieldName);
            }

            return classes;
        },

        fieldValueFileBoxId() {
            const fieldValueFileBoxId = this.cell.value?.fieldValueFileBoxId || '';

            if (!fieldValueFileBoxId) return null;


            return parseInt(`${fieldValueFileBoxId}`?.match(/\d/g).join(''), 10);
        },

        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.set.field.value.pro.tip.link.keap') : this.$t('funnel.set.field.value.pro.tip.link.classic');
        },
    },

    watch: {
        'config.fieldName': {
            deep: true,
            handler(fieldName, oldFieldName) {
                if (oldFieldName && oldFieldName !== fieldName) {
                    this.config.fieldValue = '';
                    this.fieldConfigRender = '';
                }

                if (fieldName !== COMPANY_INFO) {
                    this.companyNameFieldValue = '';
                    this.companyFieldOptions = [];
                }

                this.onUpdateFieldName(fieldName);
                this.$emit('mark-dirty');
            },
        },

        companyNameFieldValue: {
            deep: true,
            handler(companyName, oldCompanyName) {
                if (companyName !== oldCompanyName) {
                    this.companyFieldOptions = [];
                }
                this.updateCompanyFieldOptions(companyName);
            },
        },
    },
    async created() {
        await this.loadFields();
    },
    mounted () {
        this.init();
    },
    methods: {
        async loadFields() {
            this.fields = await getContactFields();
        },

        async fetchAndSetCompanyOptions(companyName = '') {
            if (!companyName) return;

            this.companyLoading = true;

            try {
                const { data } = await axios.get('/app/funnel/companies', { params: { companyName } });

                this.companyFieldOptions = data;
            } catch (err) {
                this.$toast({ message: this.$t('funnel.set.field.value.save.error') });
            }

            this.companyLoading = false;
        },

        async fetchCompanyById(companyId) {
            if (!companyId) return null;

            this.companyLoading = true;

            try {
                const { data } = await axios.get(`/app/funnel/companies/${companyId}`);

                this.companyLoading = false;

                return data;
            } catch (err) {
                this.$toast({ message: this.$t('funnel.set.field.value.save.error') });

                this.companyLoading = false;

                return null;
            }
        },

        async init() {
            await this.fields;

            this.updateCompanyFieldOptions = debounce(this.fetchAndSetCompanyOptions, 300, { trailing: true });

            const cell = { ...this.cell };

            cell.value = cell.value || {};

            if (this.fieldValueFileBoxId) {
                this.config = await getFieldValueConfig(this.fieldValueFileBoxId);

                if (this.config.fieldDataType === 'dateTime') {
                    this.config.fieldDataType = 'datetime';
                }

                if (this.config.overwriteExistingValue && !this.config.clearExistingValue && !this.config.mathOption) {
                    this.option = OPTION_VALUES.OVERWRITE;
                } else if (this.config.overwriteExistingValue && this.config.clearExistingValue) {
                    this.option = OPTION_VALUES.CLEAR;
                } else if (this.config.overwriteExistingValue && !!this.config.mathOption) {
                    this.option = OPTION_VALUES.MATH;
                } else {
                    this.option = OPTION_VALUES.SET;
                }

                if (this.isCompanyField) {
                    const company = await this.fetchCompanyById(this.config.fieldValue);

                    this.companyFieldOptions = [company];
                    this.companyNameFieldValue = company.name;
                }
            }

            this.onUpdateFieldName(this.config.fieldName);
        },

        async afterFill() {
            await this.$nextTick();

            this.config.fieldDataType = null;

            if (!this.$refs.fieldConfigRender) return;

            const { fieldType } = this.getSelectedFieldDetails();

            this.config.fieldDataType = fieldType;
            this.$refs.fieldConfigRender.querySelectorAll('script').forEach((script) => script.remove());

            // hide permission check - this is the price of 'backend rendering' the field configs
            const permissionCheckElement = this.$refs.fieldConfigRender.querySelector(`#permissionCheck_${this.config.fieldName.replace('[', '\\[').replace(']', '\\]')}`);

            if (permissionCheckElement) permissionCheckElement.style.display = 'none';

            // note: this is bad but it's better than hijacking the date picker to shove event handlers on it. The previous implementation was jQuery.
            const datePicker = this.$refs.fieldConfigRender.querySelector('.inf-date-picker');

            if (datePicker) {
                const [date, time] = (this.config.fieldValue || '').split(' ');

                if (datePicker.type !== 'date') {
                    datePicker.type = 'date';
                }

                datePicker.value = date;

                datePicker.closest('.inf-date-time,.infusion-calendar-wrapper').querySelector('.np').style.display = 'none';

                this.config.fieldType = 'date';

                const timePicker = this.$refs.fieldConfigRender.querySelector('.inf-time-picker');

                if (timePicker) {
                    timePicker.value = time;
                    this.config.fieldType = 'time';
                }
            } else {
                const hasRadioButton = !!this.$refs.fieldConfigRender.querySelector('[type="radio"]');

                if (hasRadioButton) {
                    const selectedRadio = this.$refs.fieldConfigRender.querySelector(`[type="radio"][value="${this.config.fieldValue}"]`);

                    if (selectedRadio) selectedRadio.checked = true;
                } else {
                    const selectedElement = this.$refs.fieldConfigRender.querySelector('input,select,textarea');

                    if (selectedElement) selectedElement.value = this.config.fieldValue;
                }
            }
        },

        getSelectedFieldDetails() {
            const { fieldName } = this.config;
            const fieldElement = this.$refs.fieldConfigRender.querySelector('input,select,textarea');

            let fieldType = fieldElement?.attr || fieldElement?.prop;

            if (this.showAdditionalFieldConfiguration && !this.isCompanyField && !this.isPersonNotesField) {
                if (this.$refs.fieldConfigRender.querySelector('.inf-datetime-value')) {
                    fieldType = 'datetime';
                } else if (this.$refs.fieldConfigRender.querySelector('.inf-date-picker')) {
                    if (this.$refs.fieldConfigRender.querySelector('.inf-time-picker')) {
                        fieldType = 'datetime';
                    } else {
                        fieldType = 'date';
                    }
                }
            }

            let fieldValue;

            const hasRadioButton = !!this.$refs.fieldConfigRender.querySelector('[type="radio"]');

            if (hasRadioButton) {
                fieldType = 'radio';
                fieldValue = this.$refs.fieldConfigRender.querySelector('[type="radio"]:checked')?.value;
            } else {
                fieldValue = fieldElement?.value;
            }

            return { fieldType, fieldValue, fieldName };
        },

        async saveCell() {
            const hasInvalidFieldName = !this.option || !this.config.fieldName;

            if (!hasInvalidFieldName) this.bindNewFieldValueToSave();

            const hasInvalidFieldValue = [OPTION_VALUES.SET, OPTION_VALUES.OVERWRITE].includes(this.option) && !this.config.fieldValue && !this.config.useTodaysDate;
            const hasInvalidMath = this.option === OPTION_VALUES.MATH && (!this.config.mathOption || !this.config.fieldValue);

            this.showError = hasInvalidFieldName || hasInvalidFieldValue || hasInvalidMath;

            if (this.showError) return;

            const cell = { ...this.cell };

            cell.value = cell.value || {};

            const config = {
                fieldName: this.config.fieldName,
                fieldValue: this.config.fieldValue,
                overwriteExisting: this.config.overwriteExistingValue,
                clearExisting: this.config.clearExistingValue,
                useTodaysDate: this.config.useTodaysDate,
                mathOption: this.config.mathOption,
            };

            try {
                const responseText = await save(cell.value.fieldValueFileBoxId || 0, config);

                cell.value.fieldValueFileBoxId = cell.value.fieldValueFileBoxId || responseText;

                this.$emit('update-cell-value', { cell });
            } catch (error) {
                this.$toast({ message: this.$t('funnel.set.field.value.save.error') });
            }
        },

        async onUpdateFieldName(fieldName) {
            if (!fieldName || !this.showAdditionalFieldConfiguration) return;

            this.fieldConfigRender = this.isCompanyField || this.isPersonNotesField
                ? null
                : await loadFieldValueCode({ fieldName, fieldValue: '' });

            this.afterFill();
        },

        bindNewFieldValueToSave() {
            if ([OPTION_VALUES.MATH, OPTION_VALUES.CLEAR].includes(this.option)) return; // MATH already has fieldValue bound correctly; CLEAR doesn't use fieldValue
            if (this.isCompanyField) return; // COMPANY is bound correctly - doesn't need to scrape values
            if (this.isPersonNotesField) return; // PERSON NOTES is bound correctly - doesn't need to scrape values

            const selectedFieldValueDetails = this.getSelectedFieldDetails();

            if (this.config.useTodaysDate) {
                selectedFieldValueDetails.fieldValue = null;
            } else if (selectedFieldValueDetails.fieldType === 'datetime') {
                const datePickerElement = this.$refs.fieldConfigRender.querySelector('.inf-date-picker');
                const timePickerElement = this.$refs.fieldConfigRender.querySelector('.inf-time-picker');

                if (timePickerElement && datePickerElement?.value) {
                    selectedFieldValueDetails.fieldValue = timePickerElement.value
                        ? `${datePickerElement?.value} ${timePickerElement?.value}`
                        : null;
                } else {
                    selectedFieldValueDetails.fieldValue = datePickerElement?.value;
                }
            }

            if (selectedFieldValueDetails.fieldType === FIELD_TYPES.SELECT_MULTIPLE) {
                if (selectedFieldValueDetails.fieldValue != null) {
                    selectedFieldValueDetails.fieldValue = selectedFieldValueDetails.fieldValue.join(',');
                }
            }

            this.config.fieldValue = selectedFieldValueDetails.fieldValue;
        },

        closeModal() {
            this.$emit('close-modal');
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.set-field-value-type-select {
    margin-bottom: 0;
    &:only-child {
        margin-bottom: $spacing-200;
    }
}
.set-field-value-type-select .set-field-value-option {
    display: grid;
    grid-template-columns: 1fr;
    padding: $spacing-100 $spacing-200;
}

.field-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    grid-gap: $spacing-100;
    margin-top: $spacing-200;

    .field-option-select {
        margin-top: $spacing-200;
    }
    .math-config-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: $spacing-200;
        height: max-content;
        margin: $spacing-200 auto 0;
    }
}

.error-message {
    color: $color-red-800;
    font-weight: $font-weight-regular;
    font-size: $font-size-xs;
    line-height: $line-height-sm;
    display: inline-block;
}
.error-message::after {
    content: "\A\A";
    white-space: pre;
}

.permission-checkbox-label {
    display: none;
}

.person-notes-textarea {
    grid-column: span 2;
    grid-row: 2;
}

.company-field-search-container {
    display: grid;
    grid-template-columns: 1fr $spacing-500;
    height: fit-content;
    margin: $spacing-200 0;

    .input-field {
        grid-column: span 2;
        margin-bottom: $spacing-200;
    }

    .multiselect.searchable {
        margin-bottom: 0;

        &:last-child {
            grid-column: span 2;
        }
    }

    .no-company-warning {
        text-align: center;
    }
}

.set-field-value-process-modal {
    .option-description {
        height: 16px;
        align-self: stretch;
        flex-grow: 0;
        font-size: $font-size-xs;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.33;
        letter-spacing: normal;
        text-align: left;
        color: $color-ink-800;
    }
}
</style>

<style lang="scss" rel="stylesheet/scss">

.set-field-value-process-modal {
    .multiselect-single > input:focus + label {
        margin-top: 0;
    }

    .multiselect-single > label.static-label {
        margin-top: 1px;
    }

    .select-container > label {
        margin-top: -#{$spacing-075};
    }

    .select-field.active label {
        margin-top: 0;
    }

    .select-field.has-selected label {
        margin-top: 0;
    }

    div#field-config-render.field-class-short {
        width: 100%;
        margin-top: -#{$spacing-400};
        grid-area: field-value-config;
    }

    .math-config-container {
        display: grid;
        grid-template-columns: auto auto;
    }

    .inf-field-wrapper span > label {
        position: relative;
        font-size: $font-size-xs;
        background-color: $color-paper;
        top: $spacing-200;
        left: $spacing-125;
        padding: 0 $spacing-075 0 $spacing-075;
        z-index: 100;
        white-space: nowrap;
    }

    .inf-field-wrapper span > input,
    .inf-field-wrapper span div > input,
    .inf-field-wrapper span > select {
        display: block;
        padding: $spacing-125 0 $spacing-125 $spacing-200;
        width: 100%;
        height: 40px !important;
        border: 1px solid $color-ink-200;
        font-size: $font-size-sm;
        -moz-appearance: none;
        -webkit-appearance: none;
        appearance: none;
        background-color: $color-paper;
        background-size: 12px auto, 100%;
        border-radius: $border-radius;
    }

    .inf-field-wrapper span > table {
        top: $spacing-125;
        position: relative;
        left: $spacing-150;
        width: auto;
    }

    .inf-field-wrapper span > table td {
        padding-left: $spacing-050;
        padding-bottom: $spacing-050;
    }

    .inf-field-wrapper span > div {
        width: 100%;
    }

    .inf-field-wrapper.vertical {
        width: 100%;
    }

    .inf-field-wrapper span div span a img
    , .inf-field-wrapper span span img {
        position: relative;
        bottom: $spacing-300;
        left: 265px;
    }

    .inf-field-wrapper.vertical {
        display: grid;
    }

    .inf-field-wrapper {
        padding-bottom: 0;
    }

    .inf-field-wrapper span > label {
        margin-left: -#{$spacing-050};
        font-size: $font-size-sm;
        font-weight: normal;
        padding: 0 $spacing-050;
        top: $spacing-200;
        transform: translateY(-50%);
        z-index: 1;
        width: auto;
        white-space: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.33;
        letter-spacing: normal;
        color: $color-ink-800;
    }

    .date-checkbox > .content {
        padding-top: 0;
    }

    div#field-config-render.custom_datetime {
        margin-top: -#{$spacing-200};
    }

    div#field-config-render.custom_datetime img {
        left: 224px;
    }

    div#field-config-render.custom_date {
        margin-top: -#{$spacing-250};
    }

    div#field-config-render.custom_date img {
        left: 210px;
    }

    div#field-config-render.custom_text {
        margin-top: -#{$spacing-400};
    }

    div#field-config-render.custom_textarea {
        width: 100%;
        height: 120px;
        margin-top: 6%;
        margin-bottom: 8%;
    }

    div#field-config-render.custom_website {
        margin-top: -#{$spacing-200};
    }

    div#field-config-render.custom-text-nbsp {
        margin-top: -#{$spacing-200};
    }

    div#field-config-render.custom_textarea textarea {
        width: 100%;
        border-radius: $border-radius;
    }

    div#field-config-render > .inf-field-wrapper > span.inf-label {
        width: auto;
    }

    .modal-footer {
        visibility: hidden;
        display: none;
    }

    .field-class-short .custom_radio table {
        width: auto;
    }

    .field-class-short .custom_radio {
        padding-top: $spacing-400;
    }
}
</style>
